import ApplicationAdapter from './application';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type { Snapshot } from '@ember-data/legacy-compat/-private';

type Signer = {
  role: string;
  email: string;
  order: number;
};

/**
 * An extended adapter for the `PslfForm` model.
 *
 * @memberof adapters
 */
class PslfFormAdapter extends ApplicationAdapter {
  urlForFindRecord(id: string, modelName: string, snapshot: Snapshot) {
    if (snapshot.adapterOptions?.useV2) {
      return `${this.host}/api/v2/${this.pathForType(modelName)}/${id}`;
    }
    return super.urlForFindRecord(id, modelName, snapshot);
  }

  urlForCreateRecord(modelName: string, snapshot: Snapshot) {
    if (snapshot.adapterOptions?.useV2) {
      return `${this.host}/api/v2/${this.pathForType(modelName)}`;
    }
    return super.urlForCreateRecord(modelName, snapshot);
  }

  urlForUpdateRecord(id: string, modelName: string, snapshot: Snapshot) {
    if (snapshot.adapterOptions?.useV2) {
      return `${this.host}/api/v2/${this.pathForType(modelName)}/${id}`;
    }
    return super.urlForUpdateRecord(id, modelName, snapshot);
  }

  /**
   * Loads the status of the document for the employer workflow to see if it's been signed yet.
   *
   * @param formDocumentId - the ID of the document to check
   */
  async pollForEmployerStatus(formDocumentId: string): Promise<string | undefined> {
    try {
      // @ts-expect-error stop using adapters
      const response = await this.api.get('pslf-forms/employer/invite-status', {
        params: { id: formDocumentId },
      });
      return response.data.status;
    } catch (_error) {
      return;
    }
  }

  async verifyNonPartnerEmployer(id: string, approvalId: string) {
    try {
      const url = this.buildURL(`pslf-forms`) + '/non-partner-employer/verify';
      const response = await this.ajax(url, 'POST', {
        data: {
          document_id: id,
          approvalId: approvalId,
        },
      });
      return response;
    } catch (error) {
      if (error) {
        throw new Error('Bad request. Please check the employer data.');
      }
      throw new Error('An unexpected error occurred while verifying the non-partner employer.');
    }
  }

  /**
   * Prepares a form for a NON-PARTNER employer signature by filling it in with the values contained in
   * `entity`, updating the store with an updated entity that contains the `embedUrl`.
   *
   * @param {string} approvalId - The hashed approval ID for this form
   * @param {PslfFormModel} formData  - The form to prepare for signature
   */
  async prepareForNonPartnerEmployerSignature(
    approvalId: string,
    formData: Record<string, string | number>
  ) {
    const url = this.buildURL('pslf-forms') + '/non-partner-employer/embed-document';
    const response = await this.ajax(url, 'POST', {
      data: {
        approvalId: approvalId,
        employerApartment: formData.employerApartment,
        employerCity: formData.employerCity,
        employerFein: formData.employerEin,
        employerName: formData.employerName,
        employerState: formData.employerState,
        employerStreetAddress: formData.employerStreetAddress,
        employerWebsite: formData.employerWebsite,
        employerZip: formData.employerZip,
        employmentContinues: formData.employmentContinues,
        employmentEndDate: formData.employmentEndDate,
        employmentHours: formData.employmentHours,
        employmentStartDate: formData.employmentStartDate,
        employmentStatus: formData.employmentStatus,
        id: formData.id,
      },
    });
    // @ts-expect-error stop using adapters
    return { embedUrl: response.embedUrl, status: response.status };
  }

  /**
   * @param {PslfForm}  pslfForm - the PslfForm model for which to perform the action
   * @param {string} actionEndpoint - the server endpoint to append (ex. '/cancel')
   * @return {PslfForm}
   */
  async triggerFormActionAndReload(
    pslfForm: PslfFormModel,
    actionEndpoint: string,
    options: { syncReindex?: boolean } = {}
  ) {
    if (!pslfForm) {
      throw new Error('PSLF form is required');
    }
    const url = this.buildURL('pslf-form', pslfForm.id) + actionEndpoint;

    // Add X-Reindex-Mode header for synchronous reindexing when needed
    const headers = options.syncReindex ? { 'X-Reindex-Mode': 'sync' } : undefined;

    await this.ajax(url, 'PATCH', headers ? { headers } : undefined);
    const updatedForm = await pslfForm.reload();
    return updatedForm;
  }

  async signBorrower(pslfForm: PslfFormModel) {
    return this.triggerFormActionAndReload(pslfForm, '/sign-borrower', { syncReindex: true });
  }

  async signEmployer(pslfForm: PslfFormModel) {
    // Use sync reindexing for employer sign to ensure table refreshes immediately
    return this.triggerFormActionAndReload(pslfForm, '/sign-employer', { syncReindex: true });
  }

  /**
   * @param {PslfForm}  pslfForm - the PslfForm model to cancel
   * @return {PslfForm}
   */
  async cancel(pslfForm: PslfFormModel) {
    return this.triggerFormActionAndReload(pslfForm, '/cancel', { syncReindex: true });
  }

  /**
   * Reject a PSLF form with an optional comment
   * Comment is stored in PaperTrail's _changelog field
   * @param {PslfForm}  pslfForm - the PslfForm model to reject
   * @param {string} comment - reason for rejection
   * @return {PslfForm}
   */
  async reject(pslfForm: PslfFormModel, comment = '') {
    if (!pslfForm) {
      throw new Error('PSLF form is required');
    }

    const url = this.buildURL('pslf-form', pslfForm.id) + '/reject';

    // Send _changelog in the request body
    // PaperTrail's info_for_paper_trail will extract it automatically
    // Also send X-Reindex-Mode header to force synchronous reindexing for immediate UI update
    await this.ajax(url, 'PATCH', {
      headers: {
        'X-Reindex-Mode': 'sync',
      },
      data: {
        data: {
          type: 'pslf-forms',
          id: pslfForm.id,
          attributes: {
            _changelog: comment,
          },
        },
      },
    });

    return await pslfForm.reload();
  }

  /**
   * @param {PslfForm}  pslfForm - the PslfForm model for which to send reminder
   * @return {PslfForm}
   */
  async resendEmployerEmail(pslfForm: PslfFormModel) {
    return this.triggerFormActionAndReload(pslfForm, '/notify-employer-sign-reminder');
  }

  /**
   * Prepares a form for an employer signature by filling it in with the values contained in
   * `entity`, updating the store with an updated entity that contains the `embedUrl`.
   *
   * @param pslfForm  - The form to prepare for signature
   */

  async prepareForEmployerSignature(pslfForm: PslfFormModel) {
    const url = this.buildURL('pslf-forms') + `/employer/embed-document`;
    const response = await this.ajax(url, 'POST', {
      data: {
        id: pslfForm.documentId,
        employmentHours: pslfForm.employerFields['employment-hours'],
        employmentStartDate: pslfForm.employerFields['employment-start-date'],
        employmentEndDate: pslfForm.employerFields['employment-end-date'],
        employmentContinues: pslfForm.employerFields['employment-continues'],
        employmentStatus: pslfForm.employerFields['employment-status'],
      },
    });
    // @ts-expect-error stop using adapters
    return { embedUrl: response.embedUrl, status: response.status };
  }

  async prepareForBorrowerSignature(
    pslfForm: PslfFormModel,
    signers: [Signer, Signer],
    templateId: string,
    borrowerSsn: string
  ) {
    const url = this.buildURL('pslf-forms') + `/${pslfForm.id}/borrower-embed-document`;
    const response = await this.ajax(url, 'POST', {
      data: {
        templateId,
        signers,
        currentSigner: 'Borrower',
        borrowerSsn,
        useNewEmbedFlow: true,
      },
    });
    // @ts-expect-error stop using adapters
    return { embedUrl: url, status: response.status };
  }

  async prepareForBorrowerPreApprovedSignature(
    pslfForm: PslfFormModel,
    signers: [Signer],
    templateId: string,
    borrowerSsn: string
  ) {
    const url =
      this.buildURL('pslf-forms') + `/${pslfForm.id}/borrower-pre-approved-embed-document`;
    const response = await this.ajax(url, 'POST', {
      data: {
        templateId,
        signers,
        currentSigner: 'Borrower',
        borrowerSsn,
        useNewEmbedFlow: true,
      },
    });
    // @ts-expect-error stop using adapters
    return { embedUrl: url, status: response.status };
  }

  async signNonPartnerEmployer(
    approvalId: string,
    documentId: string,
    signature: string,
    employerFields: Record<string, string | number | boolean>
  ) {
    const url = `${this.host}/api/v2/pslf-forms/sign-non-partner-employer`;

    const response = await this.ajax(url, 'POST', {
      data: {
        approvalId,
        documentId,
        signature,
        employerFields,
      },
    });
    return response;
  }

  async transferPdf(pslfForm: PslfFormModel) {
    const url = this.buildURL('pslf-forms') + `/${pslfForm.id}/transfer-pdf`;
    return await this.ajax(url, 'PATCH');
  }
}

export default PslfFormAdapter;
